import type { StateCreator } from 'zustand'
import type { authSessionState } from './authSessionSlice'
// UI-84: commenting is one of the actions that makes a board worth keeping, so
// this slice calls into the board slice. Declared in the composite below
// rather than reached with a cast, so a rename of `keepBoard` is a type error
// here instead of a silently dead optional call.
import type { BoardSliceState } from './boardSlice'
import { apiService } from '@/service/api'
import type {
  Comment,
  CreateCommentRequest,
  UpdateCommentRequest,
  CommentFilters,
} from '@/types/comment'
import { toast } from 'sonner'

export interface CommentState {
  // State
  comments: Record<string, Comment[]> // Keyed by workspace_id
  isLoadingComments: boolean
  replyingTo: string | null // Comment ID being replied to
  editingComment: string | null // Comment ID being edited

  // Actions
  loadComments: (workspaceId: string, filters?: CommentFilters) => Promise<void>

  createComment: (
    workspaceId: string,
    data: CreateCommentRequest
  ) => Promise<Comment>

  updateComment: (
    commentId: string,
    data: UpdateCommentRequest
  ) => Promise<Comment>

  deleteComment: (commentId: string) => Promise<void>

  resolveComment: (commentId: string, isResolved: boolean) => Promise<Comment>

  setReplyingTo: (commentId: string | null) => void
  setEditingComment: (commentId: string | null) => void

  // Helper to update a comment in the state
  updateCommentInState: (workspaceId: string, updatedComment: Comment) => void

  // Helper to remove a comment from state
  removeCommentFromState: (workspaceId: string, commentId: string) => void

  // Reaction actions
  addReaction: (
    workspaceId: string,
    commentId: string,
    reactionType: string
  ) => Promise<void>

  removeReaction: (
    workspaceId: string,
    commentId: string,
    reactionType: string
  ) => Promise<void>

  toggleReaction: (
    workspaceId: string,
    commentId: string,
    reactionType: string,
    currentUserId: string
  ) => Promise<void>
}

export const initialCommentState = {
  comments: {},
  isLoadingComments: false,
  replyingTo: null,
  editingComment: null,
}

export const commentSlice: StateCreator<
  CommentState & authSessionState & Pick<BoardSliceState, 'keepBoard'>,
  [],
  [],
  CommentState
> = (set, get) => ({
  ...initialCommentState,

  loadComments: async (workspaceId: string, filters?: CommentFilters) => {
    set({ isLoadingComments: true })
    try {
      const comments = await apiService.listComments(workspaceId, filters)

      set((state) => ({
        comments: {
          ...state.comments,
          [workspaceId]: comments,
        },
        isLoadingComments: false,
      }))
    } catch (error) {
      console.error('Failed to load comments:', error)
      toast.error('Failed to load comments')
      set({ isLoadingComments: false })
    }
  },

  createComment: async (
    workspaceId: string,
    data: CreateCommentRequest
  ): Promise<Comment> => {
    try {
      const newComment = await apiService.createComment(workspaceId, data)

      // UI-84: commenting makes a board worth keeping (Hasma) — but only
      // once the comment exists. Promoting before the POST meant a failed
      // comment still saved an empty board, which is the thing being removed.
      get().keepBoard(workspaceId)

      // Optimistically add to state
      set((state) => {
        const workspaceComments = state.comments[workspaceId] || []

        // If it's a reply, we need to update the parent's replies
        if (data.parent_comment_id) {
          const updatedComments = addReplyToParent(
            workspaceComments,
            data.parent_comment_id,
            newComment
          )
          return {
            comments: {
              ...state.comments,
              [workspaceId]: updatedComments,
            },
          }
        }

        // If it's a top-level comment, just add it
        return {
          comments: {
            ...state.comments,
            [workspaceId]: [...workspaceComments, newComment],
          },
        }
      })

      return newComment
    } catch (error) {
      console.error('Failed to create comment:', error)
      throw error
    }
  },

  updateComment: async (
    commentId: string,
    data: UpdateCommentRequest
  ): Promise<Comment> => {
    try {
      const updatedComment = await apiService.updateComment(commentId, data)

      // PUT /v1/comments/:id answers with the comment alone: its `replies` is
      // always [] (comments.service.ts hands toResponseDto an empty list), so
      // spreading the response over the store entry wiped every reply the
      // thread had loaded. A pin move (UI-57) is written through here, so a
      // reply made just before the move vanished from the thread until the
      // next reload — and stayed gone if the move settled while the thread
      // was open. resolveComment already sidesteps this by patching one
      // field; here the fields PUT owns are merged and the thread is kept.
      const { replies: _repliesNotInThisResponse, ...fields } = updatedComment
      void _repliesNotInThisResponse

      // Update in state
      set((state) => {
        const newComments = { ...state.comments }
        for (const workspaceId in newComments) {
          newComments[workspaceId] = updateCommentRecursive(
            newComments[workspaceId],
            fields
          )
        }
        return { comments: newComments }
      })

      return updatedComment
    } catch (error) {
      console.error('Failed to update comment:', error)
      throw error
    }
  },

  deleteComment: async (commentId: string): Promise<void> => {
    try {
      await apiService.deleteComment(commentId)

      // Remove from state
      set((state) => {
        const newComments = { ...state.comments }
        for (const workspaceId in newComments) {
          newComments[workspaceId] = removeCommentRecursive(
            newComments[workspaceId],
            commentId
          )
        }
        return { comments: newComments }
      })
    } catch (error) {
      console.error('Failed to delete comment:', error)
      throw error
    }
  },

  resolveComment: async (
    commentId: string,
    isResolved: boolean
  ): Promise<Comment> => {
    try {
      const updatedComment = await apiService.resolveComment(
        commentId,
        isResolved
      )

      // Only update the is_resolved field in state, don't mutate the entire comment
      set((state) => {
        const newComments = { ...state.comments }
        for (const workspaceId in newComments) {
          newComments[workspaceId] = updateCommentResolvedStatus(
            newComments[workspaceId],
            commentId,
            isResolved
          )
        }
        return { comments: newComments }
      })

      return updatedComment
    } catch (error) {
      console.error('Failed to resolve comment:', error)
      throw error
    }
  },

  setReplyingTo: (commentId: string | null) => {
    set({ replyingTo: commentId })
  },

  setEditingComment: (commentId: string | null) => {
    set({ editingComment: commentId })
  },

  updateCommentInState: (workspaceId: string, updatedComment: Comment) => {
    set((state) => ({
      comments: {
        ...state.comments,
        [workspaceId]: updateCommentRecursive(
          state.comments[workspaceId] || [],
          updatedComment
        ),
      },
    }))
  },

  removeCommentFromState: (workspaceId: string, commentId: string) => {
    set((state) => ({
      comments: {
        ...state.comments,
        [workspaceId]: removeCommentRecursive(
          state.comments[workspaceId] || [],
          commentId
        ),
      },
    }))
  },

  addReaction: async (
    workspaceId: string,
    commentId: string,
    reactionType: string
  ): Promise<void> => {
    try {
      await apiService.addReaction(commentId, reactionType)

      // Find the root comment ID (either this comment or its parent)
      const { comments } = get()
      const workspaceComments = comments[workspaceId] || []

      const findRootCommentId = (
        comms: Comment[],
        targetId: string
      ): string | null => {
        for (const comm of comms) {
          if (comm.id === targetId) return comm.id // It's a root comment
          const searchInReplies = (replies: Comment[]): string | null => {
            for (const reply of replies) {
              if (reply.id === targetId) return comm.id // Found in replies, return root
              const found = searchInReplies(reply.replies)
              if (found) return found
            }
            return null
          }
          const found = searchInReplies(comm.replies)
          if (found) return found
        }
        return null
      }

      const rootCommentId = findRootCommentId(workspaceComments, commentId)

      if (rootCommentId) {
        // Reload just this comment thread to preserve nested structure
        const updatedThread = await apiService.getCommentThread(rootCommentId)
        get().updateCommentInState(workspaceId, updatedThread)
      }
    } catch (error) {
      console.error('Failed to add reaction:', error)
      throw error
    }
  },

  removeReaction: async (
    workspaceId: string,
    commentId: string,
    reactionType: string
  ): Promise<void> => {
    try {
      await apiService.removeReaction(commentId, reactionType)

      // Find the root comment ID (either this comment or its parent)
      const { comments } = get()
      const workspaceComments = comments[workspaceId] || []

      const findRootCommentId = (
        comms: Comment[],
        targetId: string
      ): string | null => {
        for (const comm of comms) {
          if (comm.id === targetId) return comm.id // It's a root comment
          const searchInReplies = (replies: Comment[]): string | null => {
            for (const reply of replies) {
              if (reply.id === targetId) return comm.id // Found in replies, return root
              const found = searchInReplies(reply.replies)
              if (found) return found
            }
            return null
          }
          const found = searchInReplies(comm.replies)
          if (found) return found
        }
        return null
      }

      const rootCommentId = findRootCommentId(workspaceComments, commentId)

      if (rootCommentId) {
        // Reload just this comment thread to preserve nested structure
        const updatedThread = await apiService.getCommentThread(rootCommentId)
        get().updateCommentInState(workspaceId, updatedThread)
      }
    } catch (error) {
      console.error('Failed to remove reaction:', error)
      throw error
    }
  },

  toggleReaction: async (
    workspaceId: string,
    commentId: string,
    reactionType: string,
    currentUserId: string
  ): Promise<void> => {
    const { comments } = get()
    const workspaceComments = comments[workspaceId] || []

    // Find the comment and check if user already reacted
    const findComment = (comms: Comment[]): Comment | null => {
      for (const comm of comms) {
        if (comm.id === commentId) return comm
        const found = findComment(comm.replies)
        if (found) return found
      }
      return null
    }

    const comment = findComment(workspaceComments)
    if (!comment) return

    const existingReaction = comment.reactions.find(
      (r) =>
        r.reaction_type === reactionType && r.user_ids.includes(currentUserId)
    )

    if (existingReaction) {
      await get().removeReaction(workspaceId, commentId, reactionType)
    } else {
      await get().addReaction(workspaceId, commentId, reactionType)
    }
  },
})

// Helper functions

function addReplyToParent(
  comments: Comment[],
  parentId: string,
  reply: Comment
): Comment[] {
  return addReplyToParentWithStatus(comments, parentId, reply).comments
}

function addReplyToParentWithStatus(
  comments: Comment[],
  parentId: string,
  reply: Comment
): { comments: Comment[]; added: boolean } {
  let added = false

  const updatedComments = comments.map((comment) => {
    if (comment.id === parentId) {
      added = true
      return {
        ...comment,
        replies: [...comment.replies, reply],
        reply_count: comment.reply_count + 1,
      }
    }
    if (comment.replies.length > 0) {
      const updatedReplies = addReplyToParentWithStatus(
        comment.replies,
        parentId,
        reply
      )
      if (updatedReplies.added) {
        added = true
        return {
          ...comment,
          replies: updatedReplies.comments,
          reply_count: comment.reply_count + 1,
        }
      }

      return comment
    }
    return comment
  })

  return { comments: updatedComments, added }
}

function updateCommentResolvedStatus(
  comments: Comment[],
  commentId: string,
  isResolved: boolean
): Comment[] {
  return comments.map((comment) => {
    if (comment.id === commentId) {
      // Only update the is_resolved field, keep everything else intact
      return { ...comment, is_resolved: isResolved }
    }
    if (comment.replies.length > 0) {
      return {
        ...comment,
        replies: updateCommentResolvedStatus(
          comment.replies,
          commentId,
          isResolved
        ),
      }
    }
    return comment
  })
}

function updateCommentRecursive(
  comments: Comment[],
  updatedComment: Partial<Comment> & Pick<Comment, 'id'>
): Comment[] {
  return comments.map((comment) => {
    if (comment.id === updatedComment.id) {
      return { ...comment, ...updatedComment }
    }
    if (comment.replies.length > 0) {
      return {
        ...comment,
        replies: updateCommentRecursive(comment.replies, updatedComment),
      }
    }
    return comment
  })
}

function removeCommentRecursive(
  comments: Comment[],
  commentId: string
): Comment[] {
  return comments
    .filter((comment) => comment.id !== commentId)
    .map((comment) => ({
      ...comment,
      replies: removeCommentRecursive(comment.replies, commentId),
    }))
}
